import { ProductCatalogMap } from '~/core/models';
import {
  ApplianceRecord,
  SelectedApplianceProductType,
  SelectedAppliances,
  SelectedSinksAmount,
} from '~/core/types/appliances';

/**
 * Given a list of side-appliance ids (side-panel sinks + on-card cooktop/range),
 * classify each against the new appliance catalog: keep if present, remap by
 * masterSku (recovered from the new catalog or the saved catalog) if an
 * equivalent exists, else remove.
 */
export const reconcileSideApplianceIds = (
  ids: number[],
  appliancesNewCatalog: { productId: number; masterSku?: string }[],
  savedCatalog?: ProductCatalogMap
): { remap: Record<number, number>; removed: number[] } => {
  const applianceByProductId = new Map(
    appliancesNewCatalog.map((appliance) => [appliance.productId, appliance])
  );
  // First occurrence wins, preserving Array.prototype.find semantics.
  const productIdByMasterSku = new Map<string, number>();

  appliancesNewCatalog.forEach((appliance) => {
    if (appliance.masterSku && !productIdByMasterSku.has(appliance.masterSku)) {
      productIdByMasterSku.set(appliance.masterSku, appliance.productId);
    }
  });

  const getMasterSkuFor = (id: number) =>
    applianceByProductId.get(id)?.masterSku ?? savedCatalog?.[id]?.masterSku;

  return ids.reduce<{ remap: Record<number, number>; removed: number[] }>(
    (acc, id) => {
      if (applianceByProductId.has(id)) {
        return acc; // already valid in the new catalog
      }

      const masterSku = getMasterSkuFor(id);
      const equivalentProductId = masterSku
        ? productIdByMasterSku.get(masterSku)
        : undefined;

      if (equivalentProductId && equivalentProductId !== id) {
        return {
          remap: { ...acc.remap, [id]: equivalentProductId },
          removed: acc.removed,
        };
      }

      return { remap: acc.remap, removed: [...acc.removed, id] };
    },
    { remap: {}, removed: [] }
  );
};

/**
 * Given the current sink selections and a reconcile result, returns the next
 * sink list with removed ids dropped and remapped ids updated.
 */
export const computeNextSinkSelections = (
  currentSinks: SelectedSinksAmount,
  remap: Record<number, number>,
  removed: number[]
): SelectedSinksAmount =>
  currentSinks
    .filter((s) => !removed.includes(s.id))
    .map((s) => (remap[s.id] !== undefined ? { ...s, id: remap[s.id] } : s));

/**
 * Given a single cooking-appliance selection map (`{ [productId]: { onCard, onShape } }`)
 * and a reconcile result, returns:
 *   - `next` — the updated selection map (removed ids dropped, remapped ids
 *     pointing to their equivalent product ids, quantities preserved)
 *   - `changed` — whether anything actually changed (so callers can skip
 *     dispatching when there is nothing to update)
 */
export const computeNextCookingApplianceSelection = (
  current: ApplianceRecord,
  remap: Record<number, number>,
  removed: number[]
): { next: ApplianceRecord; changed: boolean } => {
  const removedIds = new Set(removed);

  return Object.entries(current).reduce<{
    next: ApplianceRecord;
    changed: boolean;
  }>(
    (result, [productId, selection]) => {
      const currentId = Number(productId);

      if (removedIds.has(currentId)) {
        result.changed = true;

        return result;
      }

      const nextId = remap[currentId] ?? currentId;

      result.next[nextId] = selection;
      result.changed ||= nextId !== currentId;

      return result;
    },
    {
      next: {},
      changed: false,
    }
  );
};

/**
 * Returns the cooking-appliance entries (cooktop and range) whose selections
 * changed after reconciliation with the new catalog. Only entries where
 * `changed === true` are included.
 */
export const computeChangedCookingAppliances = (
  selectedCooktop: SelectedAppliances,
  selectedRange: SelectedAppliances,
  remap: Record<number, number>,
  removed: number[]
): Array<{
  selectedType: SelectedApplianceProductType;
  next: ApplianceRecord;
}> => {
  const pairs: [SelectedApplianceProductType, ApplianceRecord][] = [
    ['selectedCooktopApplianceProduct', selectedCooktop.selected],
    ['selectedRangeApplianceProduct', selectedRange.selected],
  ];

  return pairs
    .map(([selectedType, current]) => {
      const { next, changed } = computeNextCookingApplianceSelection(
        current,
        remap,
        removed
      );

      return changed ? { selectedType, next } : null;
    })
    .filter(
      (
        entry
      ): entry is {
        selectedType: SelectedApplianceProductType;
        next: ApplianceRecord;
      } => entry !== null
    );
};
